import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Modal, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Common from "../../Common/Common";
import { clusterBaseName, matchingCandidate } from "../../Common/placeNaming";
import PhotoViewer from "./PhotoViewer";
import MapPickDialog from "./MapPickDialog";
import { reverseGeocodeCoords, findIdentifyCandidates, nearestOtherCluster } from "../../Common/placeIdentify";
import IdentifyPlace, { formatDist } from "./IdentifyPlace";

const THUMB_W = 90;
const THUMB_H = 80;

function resolveUrl(url, thumb) {
  // Prefer the gdrive: → lh3.googleusercontent.com form over photo.thumb:
  // thumb is Google's own thumbnailLink from the Drive API, which is
  // session/token-scoped and unreliably hotlinkable cross-origin, unlike the
  // lh3.googleusercontent.com/d/{fileId} form (same fix used by
  // Common.getImageURL and PlaceCard.js/PlaceEditModal.js elsewhere).
  if (url && url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  if (thumb) return thumb;
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `http://liquidguides.com//Media/Photos/${url}`;
}

function photoName(photo) {
  let n = photo.name || "New Place";
  if (n.toUpperCase().includes(".JPG")) n = n.slice(0, -4);
  return n;
}

// Working clusters are built fresh from the `clusters` prop (array of
// arrays of photo objects) whenever it changes — see the prevClusters
// pattern below, mirrored from UseMyPhotosReviewModal/AddPhotosReviewModal.
function buildWorkingClusters(clusters) {
  return clusters.map((photos) => ({
    photos,
    coverIdx: 0,
    includedIdxs: new Set(photos.map((_, i) => i)),
  }));
}

function Thumb({ src, largeSrc, name, included, isCover, isGuideCover, onToggleInclude, onSetCover, onSplitOut, onToggleGuideCover, showSplit, onEnlarge }) {
  // A larger preview when the mouse rests on the photo for half a second —
  // passing over doesn't trigger it (4.10.55). Click 🔍 for full size.
  const [hover, setHover] = useState(null);
  const timer = React.useRef(null);
  const onEnter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHover({ left: Math.min(r.right + 8, window.innerWidth - 330), top: Math.max(8, Math.min(r.top - 60, window.innerHeight - 330)) }), 500);
  };
  const onLeave = () => {
    clearTimeout(timer.current);
    setHover(null);
  };
  React.useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <div
      style={{
        borderRadius: 8,
        border: isCover ? "3px solid #16a34a" : included ? "3px solid #2563eb" : "3px solid transparent",
        background: "#f3f4f6",
        overflow: "hidden",
        width: THUMB_W,
        flexShrink: 0,
        position: "relative",
        opacity: included ? 1 : 0.45,
      }}
    >
      {/* Drawn on the page itself (a portal): inside the tile it was clipped
          and placed relative to the dialog. */}
      {hover && src ? createPortal(
        <img
          className="pjr-hover-preview"
          src={largeSrc || src}
          alt=""
          referrerPolicy="no-referrer"
          style={{ position: "fixed", left: hover.left, top: hover.top, width: 320, maxHeight: 320, objectFit: "contain", background: "#000", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", zIndex: 70000, pointerEvents: "none" }}
        />,
        document.body
      ) : null}
      <div onClick={onToggleInclude} onMouseEnter={onEnter} onMouseLeave={onLeave} style={{ cursor: "pointer" }} title={name}>
        {src
          ? <img src={src} alt="" referrerPolicy="no-referrer" style={{ width: THUMB_W, height: THUMB_H, objectFit: "cover", display: "block" }} />
          : <div style={{ width: THUMB_W, height: THUMB_H, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 11 }}>No photo</div>
        }
      </div>
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        background: "rgba(0,0,0,0.55)", display: "flex",
      }}>
        <button
          onClick={onSetCover}
          title="Use as cover photo"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: isCover ? "#4ade80" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          {isCover ? "★ Cover" : "☆"}
        </button>
        <button
          onClick={onToggleGuideCover}
          title="Use as the guide's cover photo"
          style={{
            flex: 1, border: "none", background: "none", cursor: "pointer",
            color: isGuideCover ? "#f59e0b" : "#fff", fontSize: 11, padding: "3px 0",
          }}
        >
          ⌂
        </button>
        <button
          className="pjr-enlarge"
          onClick={onEnlarge}
          title="See this photo full size"
          aria-label="See this photo full size"
          style={{ border: "none", background: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "3px 5px" }}
        >
          🔍
        </button>
        {showSplit && (
          <button
            onClick={onSplitOut}
            title="Split out as new place"
            style={{ border: "none", background: "none", cursor: "pointer", color: "#fff", fontSize: 11, padding: "3px 5px" }}
          >
            ✂
          </button>
        )}
      </div>
    </div>
  );
}

export default function PhotoJournalReviewModal({ show, onHide, clusters, sensitivity, onSensitivityChange, maxSpan, onMaxSpanChange, onApply }) {
  const [workingClusters, setWorkingClusters] = useState(() => buildWorkingClusters(clusters));
  const [baseName, setBaseName] = useState("");
  // The guide's own cover photo — distinct from any place's ★ cover. Single
  // global selection across all clusters/photos, tracked by photo object
  // identity, same pattern as mobile's photoJournalGuideCoverPhoto.
  const [guideCoverPhoto, setGuideCoverPhoto] = useState(null);

  // Runs the exact same dedup HomeHeader.js's/MobileBottomNavBar.js's
  // _applyPhotoJournalClusters uses, so the preview shown here never
  // promises a name (e.g. "Japan_1") that collides with a place already in
  // the guide — e.g. from an earlier run of this same wizard.
  const previewNames = useMemo(() => {
    const trimmed = baseName.trim();
    if (!trimmed) return null;
    const used = new Set((window.guide_obj?.places?.placesList || []).map((p) => (p.Name || "").toLowerCase()));
    return workingClusters.map((_, i) => Common.uniquePlaceName(`${trimmed}_${i + 1}`, used));
  }, [baseName, workingClusters]);

  // Per-cluster "Identify place" results, keyed by cluster index:
  // { status: "loading"|"done", city, candidates, pickedTitle }. Opt-in —
  // fetched only when the user asks for a given cluster, since each run
  // costs a (billed) Google Geocoding call plus a free Wikipedia geosearch.
  const [identify, setIdentify] = useState({});
  // "Pick on the map": { ci, lat, lng } of the place being named.
  const [mapPick, setMapPick] = useState(null);
  // Full-size viewer: { ci, pi } — the place and photo it opened on.
  const [viewer, setViewer] = useState(null);

  const [prevClusters, setPrevClusters] = useState(clusters);
  if (clusters !== prevClusters) {
    setPrevClusters(clusters);
    setWorkingClusters(buildWorkingClusters(clusters));
    setGuideCoverPhoto(null);
    setIdentify({});
  }

  // skipCandidates: bulk identify only ever uses the resolved city for
  // naming (never auto-picks a landmark), so fetching Wikipedia candidates
  // there was pure wasted work — fetched but never looked at unless the user
  // later opens that specific row. Skipping it also means bulk mode is only
  // waiting on the single (paid, presumably faster/more consistent) Google
  // reverse-geocode call per cluster instead of Promise.all-ing that against
  // Wikipedia's free/best-effort geosearch, which was the slower of the two
  // in practice. candidates is set to null (not []) to distinguish "never
  // searched" from "searched, found nothing" — IdentifyPlace.js uses that to
  // offer an on-demand landmark search instead of a misleading "no named
  // places found" for a cluster that was simply never asked. Found
  // 2026-09-24.
  const runIdentify = async (ci, { skipCandidates = false } = {}) => {
    setIdentify((prev) => ({ ...prev, [ci]: { status: "loading" } }));
    const cluster = workingClusters[ci];
    const cover = cluster.photos[cluster.coverIdx];
    const lat = cover.exif_data.latitude;
    const lng = cover.exif_data.longitude;
    const [{ city }, candidates] = await Promise.all([
      reverseGeocodeCoords(lat, lng),
      skipCandidates ? Promise.resolve(null) : findIdentifyCandidates(lat, lng),
    ]);
    setIdentify((prev) => ({ ...prev, [ci]: { status: "done", city, candidates, pickedTitle: matchingCandidate(currentName(ci), candidates) } }));
  };

  // On-demand landmark search for a cluster bulk identify already resolved a
  // city for but skipped candidates on — reuses the already-known lat/lng,
  // no need to re-run the reverse geocode.
  const findCandidatesOnly = async (ci) => {
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], status: "loading" } }));
    const cluster = workingClusters[ci];
    const cover = cluster.photos[cluster.coverIdx];
    const lat = cover.exif_data.latitude;
    const lng = cover.exif_data.longitude;
    const candidates = await findIdentifyCandidates(lat, lng);
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], status: "done", candidates, pickedTitle: prev[ci]?.pickedTitle || matchingCandidate(currentName(ci), candidates) } }));
  };

  // Picking a result (or the city, or "None of these") is the newest choice, so it also replaces a name typed in the
  // place's box — a typed name wins over a pick, and picks seemed to do nothing (2026-11-02).
  const pickCandidate = (ci, title) => {
    setIdentify((prev) => ({ ...prev, [ci]: { ...prev[ci], pickedTitle: title } }));
    setWorkingClusters((prev) => prev.map((c, i) => (i === ci ? { ...c, typedName: title || "" } : c)));
  };
  // The name a place has before a result is picked: typed, else the photo's own name.
  const currentName = (ci) => {
    const c = workingClusters[ci];
    return c ? clusterBaseName({ typed: c.typedName, photo: c.photos[c.coverIdx] }) : "";
  };

  // Bulk "Identify Clusters" — runs runIdentify for every cluster that
  // hasn't already been identified, sequentially (not Promise.all) so this
  // doesn't fire a burst of billed Google Geocoding calls all at once.
  // Deliberately doesn't auto-pick a Wikipedia candidate for each cluster —
  // that stays a manual per-cluster choice via IdentifyPlace — it only
  // resolves each cluster's city, which clusterNames' priority order already
  // falls back to once a pick isn't made. Opt-in via a button, same cost
  // posture as the existing per-cluster "Identify this place" action.
  // Requested 2026-09-23.
  const [bulkIdentifying, setBulkIdentifying] = useState(false);
  const [bulkIdentifyProgress, setBulkIdentifyProgress] = useState(0);
  const identifyAllClusters = async () => {
    setBulkIdentifying(true);
    setBulkIdentifyProgress(0);
    for (let ci = 0; ci < workingClusters.length; ci++) {
      if (!identify[ci] || identify[ci].status !== "done") await runIdentify(ci, { skipCandidates: true });
      setBulkIdentifyProgress(ci + 1);
    }
    setBulkIdentifying(false);
  };

  // Final per-cluster name, in priority order: a picked Wikipedia candidate
  // > the typed base name (previewNames) > a resolved city (only once
  // Identify has run and found no pick) > the photo's own filename/"New
  // Place" default. Recomputed fresh (own `used` set) whenever any input
  // changes, mirroring previewNames' own dedup-against-existing-places
  // pattern above.
  // autoNames: what each place would be called without a typed name (the
  // box's grey hint); clusterNames: the final, unique names — a name typed
  // in the box wins (Common/placeNaming.js clusterBaseName, 4.10.55).
  const autoNames = useMemo(
    () =>
      workingClusters.map((cluster, ci) => {
        const id = identify[ci];
        return clusterBaseName({
          picked: id && id.pickedTitle,
          preview: previewNames ? previewNames[ci] : null,
          city: id && id.status === "done" ? id.city : null,
          photo: cluster.photos[cluster.coverIdx],
        });
      }),
    [identify, previewNames, workingClusters]
  );
  const clusterNames = useMemo(() => {
    const used = new Set((window.guide_obj?.places?.placesList || []).map((p) => (p.Name || "").toLowerCase()));
    return workingClusters.map((cluster, ci) =>
      Common.uniquePlaceName(clusterBaseName({ typed: cluster.typedName, picked: autoNames[ci] }), used)
    );
  }, [autoNames, workingClusters]);

  // A name typed in the place's box (they recognise it).
  const setTypedName = (ci, value) => {
    setWorkingClusters((prev) => prev.map((c, i) => (i === ci ? { ...c, typedName: value } : c)));
  };

  // Lets the user tell whether two clusters are really the same stop that
  // should be merged — see nearestOtherCluster's own comment.
  const nearestClusterInfo = useMemo(
    () => (workingClusters.length > 1 ? nearestOtherCluster(workingClusters, Common.haversineMeters) : []),
    [workingClusters]
  );

  const toggleInclude = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => prev.map((cluster, ci) => {
      if (ci !== clusterIdx) return cluster;
      if (photoIdx === cluster.coverIdx) return cluster; // cover photo must stay included
      const next = new Set(cluster.includedIdxs);
      if (next.has(photoIdx)) next.delete(photoIdx);
      else next.add(photoIdx);
      return { ...cluster, includedIdxs: next };
    }));
  };

  const setCover = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => prev.map((cluster, ci) => {
      if (ci !== clusterIdx) return cluster;
      const next = new Set(cluster.includedIdxs);
      next.add(photoIdx);
      return { ...cluster, coverIdx: photoIdx, includedIdxs: next };
    }));
  };

  const splitOut = (clusterIdx, photoIdx) => {
    setWorkingClusters((prev) => {
      const cluster = prev[clusterIdx];
      const photo = cluster.photos[photoIdx];
      const remainingPhotos = cluster.photos.filter((_, i) => i !== photoIdx);
      const next = prev.filter((_, i) => i !== clusterIdx);
      if (remainingPhotos.length > 0) {
        const remainingIncluded = new Set(
          [...cluster.includedIdxs].filter((i) => i !== photoIdx).map((i) => (i > photoIdx ? i - 1 : i))
        );
        const newCoverIdx = cluster.coverIdx === photoIdx ? 0 : cluster.coverIdx > photoIdx ? cluster.coverIdx - 1 : cluster.coverIdx;
        next.splice(clusterIdx, 0, { photos: remainingPhotos, coverIdx: newCoverIdx, includedIdxs: remainingIncluded });
      }
      next.push({ photos: [photo], coverIdx: 0, includedIdxs: new Set([0]) });
      return next;
    });
  };

  // The reverse of splitOut: folds a whole cluster's photos into the
  // cluster right above it in the list (no picker needed — this is meant
  // for the common case of a stray singleton cluster that should have
  // been grouped with its neighbor), independent of the sensitivity
  // slider so it doesn't require re-clustering everything else.
  const mergeIntoPrevious = (clusterIdx) => {
    if (clusterIdx === 0) return;
    setWorkingClusters((prev) => {
      const target = prev[clusterIdx - 1];
      const source = prev[clusterIdx];
      const offset = target.photos.length;
      const mergedPhotos = [...target.photos, ...source.photos];
      const mergedIncluded = new Set([
        ...target.includedIdxs,
        ...[...source.includedIdxs].map((i) => i + offset),
      ]);
      const next = prev.filter((_, i) => i !== clusterIdx);
      next[clusterIdx - 1] = { photos: mergedPhotos, coverIdx: target.coverIdx, includedIdxs: mergedIncluded, typedName: target.typedName };
      return next;
    });
  };

  const toggleGuideCover = (photo) => {
    setGuideCoverPhoto((prev) => (prev === photo ? null : photo));
  };

  const handleApply = () => {
    const result = workingClusters.map((cluster, ci) => ({
      coverPhoto: cluster.photos[cluster.coverIdx],
      extraPhotos: cluster.photos.filter((_, i) => i !== cluster.coverIdx && cluster.includedIdxs.has(i)),
      // Only overrides the resulting place's name — the photo's own
      // filename (and the actual file, wherever it's stored) is untouched.
      // clusterNames already resolves the full priority order (a picked
      // Identify candidate > typed base name > resolved city > filename
      // default) and is already deduped against existing places.
      placeName: clusterNames[ci],
    }));
    onApply(result, guideCoverPhoto);
  };

  const placeCount = workingClusters.length;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      {mapPick ? (
        <MapPickDialog
          show
          lat={mapPick.lat}
          lng={mapPick.lng}
          onHide={() => setMapPick(null)}
          onPick={(name) => setTypedName(mapPick.ci, name)}
        />
      ) : null}
      {viewer && workingClusters[viewer.ci] ? (
        <PhotoViewer
          photos={workingClusters[viewer.ci].photos.map((ph) => ({ src: resolveUrl(ph.url || ph.url_m, ph.thumb), name: photoName(ph) }))}
          index={viewer.pi}
          onClose={() => setViewer(null)}
        />
      ) : null}
      <Modal.Header closeButton style={{ padding: "12px 16px" }}>
        <div style={{ width: "100%" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
            Review Places — {placeCount} place{placeCount !== 1 ? "s" : ""} from your photos
          </Modal.Title>
          {/* Lives in the fixed header, not the scrollable body below, so it
              stays visible the whole time bulk identify runs — the previous
              only indicator was the "Identifying… N/M" button label, which
              scrolls out of view the moment the cluster list is scrolled,
              making a genuinely multi-second operation look like nothing was
              happening. Found 2026-09-24. */}
          {bulkIdentifying && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <FontAwesomeIcon icon={solid("spinner")} spin style={{ color: "#6b7280", fontSize: 12 }} />
              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                Identifying clusters… {bulkIdentifyProgress}/{workingClusters.length}
              </span>
              <div style={{ flex: 1, height: 4, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${workingClusters.length ? (bulkIdentifyProgress / workingClusters.length) * 100 : 0}%`,
                    background: "#6366f1", transition: "width 0.2s",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: "8px 16px" }}>
        <p style={{ fontSize: "0.8rem", color: "#666", margin: "0 0 8px" }}>
          Photos taken close together are grouped into one place. Uncheck a photo to leave it out, click ☆ to make
          it the cover photo, ⌂ to use it as the guide's own cover photo, ✂ to split it out as its own place, or
          merge a stray place into the one above it.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>Group photos within</label>
          <Form.Range
            min={0}
            max={1000}
            step={10}
            value={sensitivity}
            onChange={(e) => onSensitivityChange(parseInt(e.target.value, 10))}
            style={{ maxWidth: 200 }}
          />
          <span style={{ fontSize: "0.8rem", color: "#374151", minWidth: 60 }}>
            {sensitivity >= 1000 ? `${(sensitivity / 1000).toFixed(1)} km` : `${sensitivity} m`}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>But limit a place to</label>
          <Form.Range
            // Was floored at 200 — see PersonalizeGuideReviewModal.js's
            // identical fix for why that made this cap a no-op once
            // sensitivity was tightened below ~200m. Found 2026-09-14.
            min={50}
            max={5000}
            step={50}
            value={maxSpan}
            onChange={(e) => onMaxSpanChange(parseInt(e.target.value, 10))}
            style={{ maxWidth: 200 }}
          />
          <span style={{ fontSize: "0.8rem", color: "#374151", minWidth: 60 }}>
            {maxSpan >= 1000 ? `${(maxSpan / 1000).toFixed(1)} km` : `${maxSpan} m`} across
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "nowrap" }}>Name new places</label>
          <input
            type="text"
            value={baseName}
            onChange={(e) => setBaseName(e.target.value)}
            placeholder="e.g. Japan (optional)"
            style={{
              fontSize: "0.8rem", padding: "4px 8px", borderRadius: 6,
              border: "1px solid #d1d5db", maxWidth: 200,
            }}
          />
          {previewNames && previewNames.length > 0 && (
            <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>
              → "{previewNames[0]}"{previewNames.length > 1 ? `, "${previewNames[1]}", …` : ""}
            </span>
          )}
          <button
            onClick={identifyAllClusters}
            disabled={bulkIdentifying || workingClusters.length === 0}
            title="Reverse-geocode + Wikipedia-search every cluster below — a billed Google Geocoding call per cluster"
            style={{
              marginLeft: "auto", border: "1px solid #d1d5db", borderRadius: 6,
              background: "#fff", color: "#374151", fontSize: "0.75rem", padding: "4px 10px",
              cursor: bulkIdentifying ? "default" : "pointer", whiteSpace: "nowrap",
            }}
          >
            {bulkIdentifying ? `Identifying… ${bulkIdentifyProgress}/${workingClusters.length}` : "🔎 Identify Clusters"}
          </button>
        </div>

        {workingClusters.map((cluster, ci) => (
          <div key={ci} style={{ padding: "10px 4px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
              <input
                className="pjr-name-input"
                value={cluster.typedName || ""}
                placeholder={autoNames[ci]}
                onChange={(e) => setTypedName(ci, e.target.value)}
                aria-label={`Name for ${autoNames[ci]}`}
                title="Type a name if you recognise this place"
                style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 8px", minWidth: 0, width: 240, maxWidth: "50%" }}
              />
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>
                {cluster.includedIdxs.size} photo{cluster.includedIdxs.size !== 1 ? "s" : ""}
              </span>
              {nearestClusterInfo[ci] && (
                <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>
                  · nearest: {clusterNames[nearestClusterInfo[ci].index]} ({formatDist(nearestClusterInfo[ci].dist)})
                </span>
              )}
              {ci > 0 && (
                <button
                  onClick={() => mergeIntoPrevious(ci)}
                  title="Merge into the place above"
                  style={{
                    marginLeft: "auto", border: "1px solid #d1d5db", borderRadius: 6,
                    background: "#fff", color: "#374151", fontSize: 11, padding: "3px 8px", cursor: "pointer",
                  }}
                >
                  ⤴ Merge into previous
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {cluster.photos.map((photo, pi) => (
                <Thumb
                  key={pi}
                  src={resolveUrl(photo.url_m || photo.url, photo.thumb)}
                  largeSrc={resolveUrl(photo.url || photo.url_m, photo.thumb)}
                  onEnlarge={() => setViewer({ ci, pi })}
                  name={photoName(photo)}
                  included={cluster.includedIdxs.has(pi)}
                  isCover={pi === cluster.coverIdx}
                  isGuideCover={guideCoverPhoto === photo}
                  onToggleInclude={() => toggleInclude(ci, pi)}
                  onSetCover={() => setCover(ci, pi)}
                  onSplitOut={() => splitOut(ci, pi)}
                  onToggleGuideCover={() => toggleGuideCover(photo)}
                  showSplit={cluster.photos.length > 1}
                />
              ))}
            </div>
            <IdentifyPlace
              state={identify[ci]}
              onRun={() => runIdentify(ci)}
              onFindCandidates={() => findCandidatesOnly(ci)}
              onPick={(title) => pickCandidate(ci, title)}
              onPickOnMap={() => {
                const cover = workingClusters[ci].photos[workingClusters[ci].coverIdx];
                setMapPick({ ci, lat: cover.exif_data.latitude, lng: cover.exif_data.longitude });
              }}
            />
          </div>
        ))}
      </Modal.Body>

      <Modal.Footer style={{ padding: "10px 16px", gap: 8 }}>
        <button className="btn btn-secondary btn-sm" onClick={onHide}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleApply} disabled={placeCount === 0}>
          Create {placeCount} place{placeCount !== 1 ? "s" : ""}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
